import React, { useState } from 'react'

import { F0Box, F0Button, F0Checkbox, F0Dialog, F0Text } from '@factorialco/f0-react'
import { Add } from '@factorialco/f0-react/icons/app'
import { Reorder, useDragControls } from 'framer-motion'
import {
  AtsDeleteApplicationPhaseDocument,
  AtsHiringPipelinePhaseFragment,
  AtsReorderApplicationPhasesDocument,
} from 'generated/resources/graphql'
import { useDebounceCallback } from 'usehooks-ts'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { HiringStageBox } from 'modules/ats/components/HiringStages/HiringStageBox'
import { HiringStageWithApplicationPhases } from 'modules/ats/pages/JobPostingSettings/HiringPipeline/utils'

import { AddApplicationPhaseModal } from './AddApplicationPhaseModal'
import { ApplicationPhaseListItem } from './ApplicationPhaseListItem'
import { DeleteApplicationPhaseModal } from './DeleteApplicationPhaseModal'

interface Props {
  jobId: string
  stage: HiringStageWithApplicationPhases
}

const DraggablePhaseItem = ({
  phase,
  onDelete,
  onDragEnd,
  isDragging,
}: {
  phase: AtsHiringPipelinePhaseFragment
  onDelete: () => void
  onDragEnd: () => void
  isDragging: React.MutableRefObject<boolean>
}) => {
  const dragControls = useDragControls()

  return (
    <Reorder.Item
      value={phase}
      dragListener={false}
      dragControls={dragControls}
      onDragEnd={onDragEnd}
      style={{ listStyle: 'none', position: 'relative' }}
    >
      <div style={{ paddingBlock: 4 }}>
        <ApplicationPhaseListItem
          applicationPhase={phase}
          onDelete={onDelete}
          onDragHandlePointerDown={(e: React.PointerEvent) => {
            e.preventDefault()
            isDragging.current = true
            dragControls.start(e)
          }}
        />
      </div>
    </Reorder.Item>
  )
}

export const EditableHiringStage = ({ jobId, stage }: Props) => {
  const [addPhaseModalOpen, setAddPhaseModalOpen] = useState(false)
  const [phaseToDelete, setPhaseToDelete] = useState<AtsHiringPipelinePhaseFragment | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deletedPhaseWithApplications, setDeletedPhaseWithApplications] =
    React.useState<AtsHiringPipelinePhaseFragment | null>(null)

  const [reorderApplicationPhases] = useMutation(AtsReorderApplicationPhasesDocument, {
    extractorFn: (data) => data.atsMutations.updatePositionsAtsApplicationPhase,
  })

  const onSortPhases = useDebounceCallback(
    async (reorderedItems: AtsHiringPipelinePhaseFragment[]) => {
      const newPositions = reorderedItems.map((phase, newPosition) => ({
        atsApplicationPhaseId: phase.id,
        newPosition,
      }))
      const result = await reorderApplicationPhases({ variables: { newPositions } })

      isDragging.current = false
      hasPendingReorder.current = false

      const handled = handleResult(result, () => undefined)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
          time: 2000,
        })
      }

      return handled
    },
    500
  )

  const [deleteAtsApplicationPhase] = useMutation(AtsDeleteApplicationPhaseDocument, {
    extractorFn: (data) => data.atsMutations.deleteAtsApplicationPhase,
  })

  const onDelete = async (applicationPhase: AtsHiringPipelinePhaseFragment) => {
    const result = await deleteAtsApplicationPhase({
      variables: {
        id: applicationPhase.id,
      },
      update(cache) {
        cache.evict({ id: cache.identify(applicationPhase) })
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    }

    return handled
  }

  const [phases, setPhases] = useState(stage.applicationPhases)
  const isDragging = React.useRef(false)
  const hasPendingReorder = React.useRef(false)

  React.useEffect(() => {
    if (!isDragging.current) {
      setPhases(stage.applicationPhases)
    }
  }, [stage.applicationPhases])

  return (
    <HiringStageBox stageName={stage.name} stageLabel={stage.label}>
      {phases.length === 0 ? (
        <F0Box padding="md">
          <F0Text
            content={i18n.t('ats.job_posting_settings.hiring_pipeline.empty_hiring_phases')}
          />
        </F0Box>
      ) : (
        <Reorder.Group
          axis="y"
          values={phases}
          onReorder={(newOrder) => {
            setPhases(newOrder)
            hasPendingReorder.current = true
            onSortPhases(newOrder)
          }}
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {phases.map((phase) => (
            <DraggablePhaseItem
              key={phase.id}
              phase={phase}
              isDragging={isDragging}
              onDragEnd={() => {
                if (!hasPendingReorder.current) {
                  isDragging.current = false
                }
              }}
              onDelete={() =>
                !phase.activeApplicationsCount
                  ? setPhaseToDelete(phase)
                  : setDeletedPhaseWithApplications(phase)
              }
            />
          ))}
        </Reorder.Group>
      )}

      <F0Box display="flex" flexDirection="row" alignItems="center" paddingY="sm">
        <F0Button
          variant="ghost"
          size="sm"
          icon={Add}
          label={i18n.t('ats.job_posting_settings.hiring_pipeline.new_phase')}
          onClick={() => setAddPhaseModalOpen(true)}
        />
      </F0Box>

      {addPhaseModalOpen && (
        <AddApplicationPhaseModal
          jobId={jobId}
          hiringStage={stage}
          onClose={() => setAddPhaseModalOpen(false)}
        />
      )}

      {phaseToDelete && (
        <F0Dialog
          isOpen
          title={i18n.t('ats.job_posting_settings.hiring_pipeline.delete_phase_modal.title', {
            phaseName: phaseToDelete.name,
          })}
          description={i18n.t(
            'ats.job_posting_settings.hiring_pipeline.delete_phase_modal.description'
          )}
          onClose={() => {
            setPhaseToDelete(null)
            setConfirmDelete(false)
          }}
          primaryAction={{
            label: i18n.t('common.delete'),
            onClick: async () => {
              const result = await onDelete(phaseToDelete)
              if (result.success) {
                setPhaseToDelete(null)
                setConfirmDelete(false)
              }
            },
            variant: 'critical',
            disabled: !confirmDelete,
          }}
          secondaryAction={{
            label: i18n.t('common.cancel'),
            onClick: () => {
              setPhaseToDelete(null)
              setConfirmDelete(false)
            },
          }}
        >
          <F0Checkbox
            title={i18n.t(
              'ats.job_posting_settings.hiring_pipeline.delete_phase_modal.confirm_checkbox'
            )}
            checked={confirmDelete}
            onCheckedChange={(checked) => setConfirmDelete(checked === true)}
          />
        </F0Dialog>
      )}

      {deletedPhaseWithApplications && (
        <DeleteApplicationPhaseModal
          id={deletedPhaseWithApplications.id}
          jobId={jobId}
          onClose={() => setDeletedPhaseWithApplications(null)}
        />
      )}
    </HiringStageBox>
  )
}
